export type PollType = 'ai-insights' | 'ai-questions' | string;

export enum PollStatus {
  Pending = 'pending',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
  Timeout = 'timeout',
}

export interface Poll<TData = unknown, TMeta = Record<string, unknown>> {
  id: string;
  type: PollType;
  metadata: TMeta;
  startedAt: number;
  lastPolledAt: number;
  pollCount: number;
  status: PollStatus;
  data?: TData;
  error?: string;
  preserveHistory?: boolean;
}

export interface PollResult<TData = unknown> {
  status: PollStatus;
  data?: TData;
  shouldContinuePolling: boolean;
  error?: string;
}

export interface PollingStrategyConfig {
  // Initial interval between polls
  initialInterval?: number;
  // Maximum interval between polls
  maxInterval?: number;
  // Maximum duration for polling
  maxDuration?: number;
  // Whether to use exponential backoff
  useExponentialBackoff?: boolean;
  // Initial delay before first poll (e.g., for APIs that need processing time)
  initialDelayMs?: number;
  // Fast polling phase for time-sensitive operations
  fastPollingInterval?: number;
  // Duration of fast polling phase
  fastPollingDuration?: number;
}

export interface PollConfig<TData = unknown, TMeta = Record<string, unknown>> {
  type: PollType;
  pollFn: (poll: Poll<TData, TMeta>) => Promise<PollResult<TData>>;
  pollingStrategy?: PollingStrategyConfig;
  onComplete?: (poll: Poll<TData, TMeta>, result: PollResult<TData>) => void;
  onFailure?: (poll: Poll<TData, TMeta>, error?: Error) => void;
  onTimeout?: (poll: Poll<TData, TMeta>) => void;
}

export const DEFAULT_POLLING_STRATEGY: Required<PollingStrategyConfig> = {
  initialInterval: 5000,
  maxInterval: 30000,
  maxDuration: 5 * 60 * 1000,
  useExponentialBackoff: true,
  initialDelayMs: undefined,
  fastPollingInterval: undefined,
  fastPollingDuration: undefined,
};
